import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  Plus,
  HardHat,
  Search,
  Trash2,
  UserPlus,
  Building,
  Briefcase,
  ShieldCheck,
  CalendarDays,
  ClipboardCheck,
  Link2,
} from "lucide-react";

const WorkforcePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("contractors");
  const [contractors, setContractors] = useState([]);
  const [crews, setCrews] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [resourceAssignments, setResourceAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false);
  const [isCrewDialogOpen, setIsCrewDialogOpen] = useState(false);
  const [isWorkerDialogOpen, setIsWorkerDialogOpen] = useState(false);
  const [isAvailabilityDialogOpen, setIsAvailabilityDialogOpen] = useState(false);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);

  const [contractorForm, setContractorForm] = useState({
    name: "",
    trade: "",
    contact_email: "",
    contact_phone: "",
    specialty_ids: [],
  });

  const [crewForm, setCrewForm] = useState({
    name: "",
    subcontractor_company_id: "",
    team_type: "subcontractor",
    contact_email: "",
    specialty: "",
    capacity: 1,
    project_id: "",
    specialty_ids: [],
    task_type_ids: [],
  });

  const [workerForm, setWorkerForm] = useState({
    full_name: "",
    subcontractor_company_id: "",
    default_team_id: "",
    email: "",
    phone: "",
    worker_type: "individual",
    role_title: "",
    hourly_rate: 0,
    is_portal_enabled: false,
    specialty_ids: [],
    task_type_ids: [],
  });

  const [availabilityForm, setAvailabilityForm] = useState({
    subject_type: "team",
    subject_id: "",
    status: "available",
    start_date: "",
    end_date: "",
    notes: "",
  });

  const [bookingForm, setBookingForm] = useState({
    project_id: "",
    subcontractor_company_id: "",
    target_type: "team",
    target_id: "",
    title: "",
    description: "",
    request_start_date: "",
    request_end_date: "",
    requested_headcount: 1,
    notes: "",
  });

  const [assignmentForm, setAssignmentForm] = useState({
    project_id: "",
    booking_request_id: "",
    worker_id: "",
    team_id: "",
    assignment_role: "",
    hours_planned: 0,
    status: "planned",
  });
  const [companyLookupResults, setCompanyLookupResults] = useState([]);
  const [crewLookupResults, setCrewLookupResults] = useState([]);
  const [workerLookupResults, setWorkerLookupResults] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contractorsRes, crewsRes, workersRes, projectsRes, specialtiesRes, taskTypesRes, availabilityRes, bookingsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/contractors`),
        axios.get(`${API}/crews`),
        axios.get(`${API}/workers`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/specialties`),
        axios.get(`${API}/task-types`),
        axios.get(`${API}/availability`),
        axios.get(`${API}/booking-requests`),
        axios.get(`${API}/resource-assignments`),
      ]);
      setContractors(contractorsRes.data);
      setCrews(crewsRes.data);
      setWorkers(workersRes.data);
      setProjects(projectsRes.data);
      setSpecialties(specialtiesRes.data);
      setTaskTypes(taskTypesRes.data);
      setAvailabilityBlocks(availabilityRes.data);
      setBookingRequests(bookingsRes.data);
      setResourceAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error("Failed to load workforce data");
    } finally {
      setLoading(false);
    }
  };

  const resetContractorForm = () => setContractorForm({ name: "", trade: "", contact_email: "", contact_phone: "", specialty_ids: [] });
  const resetCrewForm = () => setCrewForm({ name: "", subcontractor_company_id: "", team_type: "subcontractor", contact_email: "", specialty: "", capacity: 1, project_id: "", specialty_ids: [], task_type_ids: [] });
  const resetWorkerForm = () => setWorkerForm({ full_name: "", subcontractor_company_id: "", default_team_id: "", email: "", phone: "", worker_type: "individual", role_title: "", hourly_rate: 0, is_portal_enabled: false, specialty_ids: [], task_type_ids: [] });
  const resetAvailabilityForm = () => setAvailabilityForm({ subject_type: "team", subject_id: "", status: "available", start_date: "", end_date: "", notes: "" });
  const resetBookingForm = () => setBookingForm({ project_id: "", subcontractor_company_id: "", target_type: "team", target_id: "", title: "", description: "", request_start_date: "", request_end_date: "", requested_headcount: 1, notes: "" });
  const resetAssignmentForm = () => setAssignmentForm({ project_id: "", booking_request_id: "", worker_id: "", team_id: "", assignment_role: "", hours_planned: 0, status: "planned" });

  const handleCompanyLookup = async () => {
    if (!contractorForm.contact_email && !contractorForm.name) {
      setCompanyLookupResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/contractors/lookup`, { params: { email: contractorForm.contact_email, name: contractorForm.name } });
      setCompanyLookupResults(response.data || []);
    } catch {
      toast.error("Failed to check existing companies");
    }
  };

  const handleLinkCompany = async (companyId) => {
    try {
      await axios.post(`${API}/contractors/link/${companyId}`);
      await fetchData();
      setIsContractorDialogOpen(false);
      resetContractorForm();
      setCompanyLookupResults([]);
      toast.success("Existing company linked to this tenant");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link company");
    }
  };

  const handleCreateContractor = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contractors`, contractorForm);
      setIsContractorDialogOpen(false);
      resetContractorForm();
      setCompanyLookupResults([]);
      await fetchData();
      toast.success(response.data?.linked_existing ? "Existing company linked" : "Subcontractor company created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create subcontractor company");
    }
  };

  const handleCrewLookup = async () => {
    if (!crewForm.subcontractor_company_id || (!crewForm.contact_email && !crewForm.name)) {
      setCrewLookupResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/crews/lookup`, { params: { company_id: crewForm.subcontractor_company_id, email: crewForm.contact_email, name: crewForm.name } });
      setCrewLookupResults(response.data || []);
    } catch {
      toast.error("Failed to check existing teams");
    }
  };

  const handleLinkCrew = async (teamId) => {
    try {
      await axios.post(`${API}/crews/link/${teamId}`);
      await fetchData();
      setIsCrewDialogOpen(false);
      resetCrewForm();
      setCrewLookupResults([]);
      toast.success("Existing team linked to this tenant");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link team");
    }
  };

  const handleCreateCrew = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...crewForm,
        project_id: crewForm.project_id || null,
      };
      const response = await axios.post(`${API}/crews`, payload);
      setIsCrewDialogOpen(false);
      resetCrewForm();
      setCrewLookupResults([]);
      await fetchData();
      toast.success(response.data?.linked_existing ? "Existing team linked" : "Team created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    }
  };

  const handleWorkerLookup = async () => {
    if (!workerForm.email && !workerForm.full_name) {
      setWorkerLookupResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/workers/lookup`, { params: { email: workerForm.email, name: workerForm.full_name } });
      setWorkerLookupResults(response.data || []);
    } catch {
      toast.error("Failed to check existing individuals");
    }
  };

  const handleCreateWorker = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...workerForm,
        default_team_id: workerForm.default_team_id || null,
      };
      const response = await axios.post(`${API}/workers`, payload);
      setIsWorkerDialogOpen(false);
      resetWorkerForm();
      setWorkerLookupResults([]);
      await fetchData();
      if (response.data?.portal_invite_sent) {
        toast.success("Individual added and portal invite sent");
      } else if (response.data?.portal_invite_url) {
        toast.success("Individual added. Portal invite created.");
      } else {
        toast.success("Individual added");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add individual");
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm("Delete this subcontractor company?")) return;
    try {
      await axios.delete(`${API}/contractors/${contractorId}`);
      setContractors(contractors.filter((c) => c.id !== contractorId));
      toast.success("Subcontractor deleted");
    } catch {
      toast.error("Failed to delete subcontractor");
    }
  };

  const handleCreateAvailability = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/availability`, availabilityForm);
      setIsAvailabilityDialogOpen(false);
      resetAvailabilityForm();
      await fetchData();
      toast.success("Availability saved");
    } catch {
      toast.error("Failed to save availability");
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/booking-requests`, bookingForm);
      setIsBookingDialogOpen(false);
      resetBookingForm();
      await fetchData();
      toast.success("Booking request created");
    } catch {
      toast.error("Failed to create booking request");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/booking-requests/${bookingId}`, { status });
      await fetchData();
      toast.success(`Booking ${status}`);
    } catch {
      toast.error("Failed to update booking status");
    }
  };

  const openBookingThread = async (bookingId) => {
    try {
      const response = await axios.post(`${API}/messages/bootstrap/booking/${bookingId}`);
      navigate(`/messages?conversation=${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open booking thread");
    }
  };

  const openAssignmentThread = async (assignmentId) => {
    try {
      const response = await axios.post(`${API}/messages/bootstrap/assignment/${assignmentId}`);
      navigate(`/messages?conversation=${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open assignment thread");
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...assignmentForm,
        booking_request_id: assignmentForm.booking_request_id || null,
        worker_id: assignmentForm.worker_id || null,
        team_id: assignmentForm.team_id || null,
      };
      await axios.post(`${API}/resource-assignments`, payload);
      setIsAssignmentDialogOpen(false);
      resetAssignmentForm();
      await fetchData();
      toast.success("Resource assignment created");
    } catch {
      toast.error("Failed to create resource assignment");
    }
  };

  const toggleSelection = (values, value) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

  const filteredContractors = contractors.filter((c) =>
    [c.name, c.trade, c.city, c.state].filter(Boolean).some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredCrews = crews.filter((c) =>
    [c.name, c.specialty, ...(c.specialties || [])].filter(Boolean).some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredWorkers = workers.filter((w) =>
    [w.full_name, w.role_title, ...(w.specialties || []), ...(w.approved_task_types || [])]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const companyMap = Object.fromEntries(contractors.map((company) => [company.id, company]));
  const projectMap = Object.fromEntries(projects.map((project) => [project.id, project]));

  const availabilitySubjects = availabilityForm.subject_type === "team" ? crews : workers;
  const bookingTargets = bookingForm.target_type === "team" ? crews.filter((crew) => !bookingForm.subcontractor_company_id || crew.contractor_company_id === bookingForm.subcontractor_company_id) : workers.filter((worker) => !bookingForm.subcontractor_company_id || worker.subcontractor_company_id === bookingForm.subcontractor_company_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading workforce data...</div>
      </div>
    );
  }

  const statCards = [
    { label: "Companies", value: contractors.length, icon: Building, color: "text-secondary", bg: "bg-secondary/10" },
    { label: "Teams", value: crews.length, icon: Users, color: "text-primary", bg: "bg-primary/10" },
    { label: "Individuals", value: workers.length, icon: UserPlus, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "Specialties", value: specialties.length, icon: ShieldCheck, color: "text-amber-500", bg: "bg-amber-500/10" },
  ];

  const MultiSelectBadges = ({ items, selected, onToggle, emptyLabel }) => (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const isSelected = selected.includes(item.id);
        return (
          <button
            type="button"
            key={item.id}
            onClick={() => onToggle(item.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              isSelected ? "bg-primary/20 border-primary text-primary" : "bg-muted/40 border-border text-muted-foreground"
            }`}
          >
            {item.name}
          </button>
        );
      })}
      {items.length === 0 && <p className="text-sm text-muted-foreground">{emptyLabel}</p>}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="workforce-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Workforce</h1>
        <p className="text-muted-foreground mt-1">
          Manage subcontractor companies, teams, individuals, specialties, and work approvals
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card className="stat-card" key={card.label}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg ${card.bg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{card.value}</p>
                    <p className="text-sm text-muted-foreground">{card.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <TabsList className="bg-muted/50 flex-wrap h-auto">
            <TabsTrigger value="contractors" className="data-[state=active]:bg-primary/20"><Building className="mr-2 h-4 w-4" />Companies</TabsTrigger>
            <TabsTrigger value="crews" className="data-[state=active]:bg-primary/20"><Users className="mr-2 h-4 w-4" />Teams</TabsTrigger>
            <TabsTrigger value="workers" className="data-[state=active]:bg-primary/20"><UserPlus className="mr-2 h-4 w-4" />Individuals</TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-primary/20"><CalendarDays className="mr-2 h-4 w-4" />Availability</TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-primary/20"><ClipboardCheck className="mr-2 h-4 w-4" />Bookings</TabsTrigger>
            <TabsTrigger value="assignments" className="data-[state=active]:bg-primary/20"><Link2 className="mr-2 h-4 w-4" />Assignments</TabsTrigger>
          </TabsList>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
            <div className="relative min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workforce..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/50"
              />
            </div>

            {activeTab === "contractors" && (
              <Dialog open={isContractorDialogOpen} onOpenChange={setIsContractorDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Add Company</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-xl">
                  <DialogHeader><DialogTitle>Add Subcontractor Company</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateContractor} className="space-y-4 mt-4">
                    <div className="space-y-2"><Label>Company Name *</Label><Input required value={contractorForm.name} onChange={(e) => setContractorForm({ ...contractorForm, name: e.target.value })} className="bg-muted/50" /></div>
                    <div className="space-y-2"><Label>Primary Trade</Label><Input value={contractorForm.trade} onChange={(e) => setContractorForm({ ...contractorForm, trade: e.target.value })} className="bg-muted/50" placeholder="Electrical" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Email</Label><Input type="email" value={contractorForm.contact_email} onChange={(e) => setContractorForm({ ...contractorForm, contact_email: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Phone</Label><Input value={contractorForm.contact_phone} onChange={(e) => setContractorForm({ ...contractorForm, contact_phone: e.target.value })} className="bg-muted/50" /></div>
                    </div>
                    <div className="flex justify-between items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm text-muted-foreground">
                      <span>Check for an existing global company before creating a new one.</span>
                      <Button type="button" variant="outline" onClick={handleCompanyLookup}>Lookup Existing</Button>
                    </div>
                    {companyLookupResults.length > 0 && <div className="space-y-2 rounded-lg border border-border/60 p-3"><Label>Matching Companies</Label>{companyLookupResults.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 rounded-md border border-border/50 p-2 text-sm"><div><div className="font-medium">{item.name}</div><div className="text-muted-foreground">{item.contact_email || "No email"}{item.trade ? ` • ${item.trade}` : ""}</div></div><Button type="button" size="sm" variant="outline" disabled={item.already_linked} onClick={() => handleLinkCompany(item.id)}>{item.already_linked ? "Already linked" : "Link existing"}</Button></div>)}</div>}
                    <div className="space-y-2"><Label>Specialties</Label><MultiSelectBadges items={specialties} selected={contractorForm.specialty_ids} onToggle={(id) => setContractorForm({ ...contractorForm, specialty_ids: toggleSelection(contractorForm.specialty_ids, id) })} emptyLabel="No specialties available" /></div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsContractorDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Save Company</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === "crews" && (
              <Dialog open={isCrewDialogOpen} onOpenChange={setIsCrewDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Add Team</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add Team</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateCrew} className="space-y-4 mt-4">
                    <div className="space-y-2"><Label>Team Name *</Label><Input required value={crewForm.name} onChange={(e) => setCrewForm({ ...crewForm, name: e.target.value })} className="bg-muted/50" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company *</Label>
                        <Select value={crewForm.subcontractor_company_id} onValueChange={(value) => setCrewForm({ ...crewForm, subcontractor_company_id: value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select company" /></SelectTrigger>
                          <SelectContent>{contractors.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Team Type</Label>
                        <Select value={crewForm.team_type} onValueChange={(value) => setCrewForm({ ...crewForm, team_type: value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                            <SelectItem value="internal">Internal</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>Team Contact Email</Label><Input type="email" value={crewForm.contact_email} onChange={(e) => setCrewForm({ ...crewForm, contact_email: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Primary Specialty</Label><Input value={crewForm.specialty} onChange={(e) => setCrewForm({ ...crewForm, specialty: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Capacity</Label><Input type="number" min="1" value={crewForm.capacity} onChange={(e) => setCrewForm({ ...crewForm, capacity: Number(e.target.value || 1) })} className="bg-muted/50" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select value={crewForm.project_id || "none"} onValueChange={(value) => setCrewForm({ ...crewForm, project_id: value === "none" ? "" : value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-between items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm text-muted-foreground">
                      <span>Check for an existing global team in this company before creating a new one.</span>
                      <Button type="button" variant="outline" onClick={handleCrewLookup}>Lookup Existing</Button>
                    </div>
                    {crewLookupResults.length > 0 && <div className="space-y-2 rounded-lg border border-border/60 p-3"><Label>Matching Teams</Label>{crewLookupResults.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 rounded-md border border-border/50 p-2 text-sm"><div><div className="font-medium">{item.name}</div><div className="text-muted-foreground">{item.contact_email || "No team email"}</div></div><Button type="button" size="sm" variant="outline" disabled={item.already_linked} onClick={() => handleLinkCrew(item.id)}>{item.already_linked ? "Already linked" : "Link existing"}</Button></div>)}</div>}
                    <div className="space-y-2"><Label>Specialties</Label><MultiSelectBadges items={specialties} selected={crewForm.specialty_ids} onToggle={(id) => setCrewForm({ ...crewForm, specialty_ids: toggleSelection(crewForm.specialty_ids, id) })} emptyLabel="No specialties available" /></div>
                    <div className="space-y-2"><Label>Approved Task Types</Label><MultiSelectBadges items={taskTypes} selected={crewForm.task_type_ids} onToggle={(id) => setCrewForm({ ...crewForm, task_type_ids: toggleSelection(crewForm.task_type_ids, id) })} emptyLabel="No task types available" /></div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsCrewDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Save Team</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === "workers" && (
              <Dialog open={isWorkerDialogOpen} onOpenChange={setIsWorkerDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Add Individual</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add Individual</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateWorker} className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Full Name *</Label><Input required value={workerForm.full_name} onChange={(e) => setWorkerForm({ ...workerForm, full_name: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Role / Title</Label><Input value={workerForm.role_title} onChange={(e) => setWorkerForm({ ...workerForm, role_title: e.target.value })} className="bg-muted/50" placeholder="Foreman" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company *</Label>
                        <Select value={workerForm.subcontractor_company_id} onValueChange={(value) => setWorkerForm({ ...workerForm, subcontractor_company_id: value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select company" /></SelectTrigger>
                          <SelectContent>{contractors.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Default Team</Label>
                        <Select value={workerForm.default_team_id || "none"} onValueChange={(value) => setWorkerForm({ ...workerForm, default_team_id: value === "none" ? "" : value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Optional" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">No team</SelectItem>{crews.map((crew) => <SelectItem key={crew.id} value={crew.id}>{crew.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Email</Label><Input type="email" value={workerForm.email} onChange={(e) => setWorkerForm({ ...workerForm, email: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Phone</Label><Input value={workerForm.phone} onChange={(e) => setWorkerForm({ ...workerForm, phone: e.target.value })} className="bg-muted/50" /></div>
                    </div>
                    <div className="flex justify-between items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm text-muted-foreground">
                      <span>Search global worker identities before creating a new tenant profile.</span>
                      <Button type="button" variant="outline" onClick={handleWorkerLookup}>Lookup Existing</Button>
                    </div>
                    {workerLookupResults.length > 0 && <div className="space-y-2 rounded-lg border border-border/60 p-3"><Label>Matching Individuals</Label>{workerLookupResults.map((item) => <div key={item.shared_worker_identity_id} className="rounded-md border border-border/50 p-2 text-sm"><div className="font-medium">{item.full_name}</div><div className="text-muted-foreground">{item.email || "No email"}{item.phone ? ` • ${item.phone}` : ""}</div><div className="text-xs text-muted-foreground mt-1">{item.already_linked ? "Already linked to this tenant" : "Will link automatically if you create with this email"}</div></div>)}</div>}
                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm text-muted-foreground">
                      If portal access is enabled and an email is provided, OMNΛS will generate a claim invite so the worker can activate their portal account.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Worker Type</Label>
                        <Select value={workerForm.worker_type} onValueChange={(value) => setWorkerForm({ ...workerForm, worker_type: value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="individual">Individual</SelectItem><SelectItem value="lead">Lead</SelectItem><SelectItem value="specialist">Specialist</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Hourly Rate</Label><Input type="number" min="0" value={workerForm.hourly_rate} onChange={(e) => setWorkerForm({ ...workerForm, hourly_rate: Number(e.target.value || 0) })} className="bg-muted/50" /></div>
                      <div className="space-y-2">
                        <Label>Portal Access</Label>
                        <Select value={workerForm.is_portal_enabled ? "enabled" : "disabled"} onValueChange={(value) => setWorkerForm({ ...workerForm, is_portal_enabled: value === "enabled" })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="disabled">Disabled</SelectItem><SelectItem value="enabled">Enabled</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2"><Label>Specialties</Label><MultiSelectBadges items={specialties} selected={workerForm.specialty_ids} onToggle={(id) => setWorkerForm({ ...workerForm, specialty_ids: toggleSelection(workerForm.specialty_ids, id) })} emptyLabel="No specialties available" /></div>
                    <div className="space-y-2"><Label>Approved Task Types</Label><MultiSelectBadges items={taskTypes} selected={workerForm.task_type_ids} onToggle={(id) => setWorkerForm({ ...workerForm, task_type_ids: toggleSelection(workerForm.task_type_ids, id) })} emptyLabel="No task types available" /></div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsWorkerDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Save Individual</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === "availability" && (
              <Dialog open={isAvailabilityDialogOpen} onOpenChange={setIsAvailabilityDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Add Availability</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-xl">
                  <DialogHeader><DialogTitle>Add Availability Block</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateAvailability} className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Subject Type</Label>
                        <Select value={availabilityForm.subject_type} onValueChange={(value) => setAvailabilityForm({ ...availabilityForm, subject_type: value, subject_id: "" })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="team">Team</SelectItem><SelectItem value="worker">Individual</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={availabilityForm.status} onValueChange={(value) => setAvailabilityForm({ ...availabilityForm, status: value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="tentative">Tentative</SelectItem>
                            <SelectItem value="unavailable">Unavailable</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Select value={availabilityForm.subject_id} onValueChange={(value) => setAvailabilityForm({ ...availabilityForm, subject_id: value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select team or individual" /></SelectTrigger>
                        <SelectContent>{availabilitySubjects.map((item) => <SelectItem key={item.id} value={item.id}>{item.name || item.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" value={availabilityForm.start_date} onChange={(e) => setAvailabilityForm({ ...availabilityForm, start_date: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>End</Label><Input type="datetime-local" value={availabilityForm.end_date} onChange={(e) => setAvailabilityForm({ ...availabilityForm, end_date: e.target.value })} className="bg-muted/50" /></div>
                    </div>
                    <div className="space-y-2"><Label>Notes</Label><Textarea value={availabilityForm.notes} onChange={(e) => setAvailabilityForm({ ...availabilityForm, notes: e.target.value })} className="bg-muted/50" /></div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsAvailabilityDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Save Availability</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === "bookings" && (
              <Dialog open={isBookingDialogOpen} onOpenChange={setIsBookingDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Request Booking</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Create Booking Request</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateBooking} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select value={bookingForm.project_id} onValueChange={(value) => setBookingForm({ ...bookingForm, project_id: value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>{projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company</Label>
                        <Select value={bookingForm.subcontractor_company_id} onValueChange={(value) => setBookingForm({ ...bookingForm, subcontractor_company_id: value, target_id: "" })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select company" /></SelectTrigger>
                          <SelectContent>{contractors.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Target Type</Label>
                        <Select value={bookingForm.target_type} onValueChange={(value) => setBookingForm({ ...bookingForm, target_type: value, target_id: "" })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="team">Team</SelectItem><SelectItem value="worker">Individual</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Target</Label>
                      <Select value={bookingForm.target_id} onValueChange={(value) => setBookingForm({ ...bookingForm, target_id: value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select target" /></SelectTrigger>
                        <SelectContent>{bookingTargets.map((item) => <SelectItem key={item.id} value={item.id}>{item.name || item.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Title</Label><Input value={bookingForm.title} onChange={(e) => setBookingForm({ ...bookingForm, title: e.target.value })} className="bg-muted/50" /></div>
                    <div className="space-y-2"><Label>Description</Label><Textarea value={bookingForm.description} onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })} className="bg-muted/50" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" value={bookingForm.request_start_date} onChange={(e) => setBookingForm({ ...bookingForm, request_start_date: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>End</Label><Input type="datetime-local" value={bookingForm.request_end_date} onChange={(e) => setBookingForm({ ...bookingForm, request_end_date: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Headcount</Label><Input type="number" min="1" value={bookingForm.requested_headcount} onChange={(e) => setBookingForm({ ...bookingForm, requested_headcount: Number(e.target.value || 1) })} className="bg-muted/50" /></div>
                    </div>
                    <div className="space-y-2"><Label>Notes</Label><Textarea value={bookingForm.notes} onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })} className="bg-muted/50" /></div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsBookingDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Create Request</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {activeTab === "assignments" && (
              <Dialog open={isAssignmentDialogOpen} onOpenChange={setIsAssignmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Assign Resource</Button>
                </DialogTrigger>
                <DialogContent className="bg-card max-w-xl">
                  <DialogHeader><DialogTitle>Create Resource Assignment</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateAssignment} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select value={assignmentForm.project_id} onValueChange={(value) => setAssignmentForm({ ...assignmentForm, project_id: value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>{projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Booking Request</Label>
                      <Select value={assignmentForm.booking_request_id || "none"} onValueChange={(value) => setAssignmentForm({ ...assignmentForm, booking_request_id: value === "none" ? "" : value })}>
                        <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">No booking request</SelectItem>{bookingRequests.map((booking) => <SelectItem key={booking.id} value={booking.id}>{booking.title}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Team</Label>
                        <Select value={assignmentForm.team_id || "none"} onValueChange={(value) => setAssignmentForm({ ...assignmentForm, team_id: value === "none" ? "" : value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Optional" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">No team</SelectItem>{crews.map((crew) => <SelectItem key={crew.id} value={crew.id}>{crew.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Individual</Label>
                        <Select value={assignmentForm.worker_id || "none"} onValueChange={(value) => setAssignmentForm({ ...assignmentForm, worker_id: value === "none" ? "" : value })}>
                          <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Optional" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">No individual</SelectItem>{workers.map((worker) => <SelectItem key={worker.id} value={worker.id}>{worker.full_name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Assignment Role</Label><Input value={assignmentForm.assignment_role} onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_role: e.target.value })} className="bg-muted/50" /></div>
                      <div className="space-y-2"><Label>Hours Planned</Label><Input type="number" min="0" value={assignmentForm.hours_planned} onChange={(e) => setAssignmentForm({ ...assignmentForm, hours_planned: Number(e.target.value || 0) })} className="bg-muted/50" /></div>
                    </div>
                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsAssignmentDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary">Create Assignment</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <TabsContent value="contractors" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredContractors.length > 0 ? filteredContractors.map((contractor) => (
              <Card key={contractor.id} className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center"><HardHat className="h-5 w-5 text-secondary" /></div>
                      <div>
                        <h3 className="font-semibold">{contractor.name}</h3>
                        <p className="text-sm text-muted-foreground">{contractor.trade || "No primary trade"}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteContractor(contractor.id)}><Trash2 size={14} className="text-destructive" /></Button>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                    {contractor.contact_email && <p>{contractor.contact_email}</p>}
                    {contractor.contact_phone && <p>{contractor.contact_phone}</p>}
                    {(contractor.city || contractor.state) && <p>{[contractor.city, contractor.state].filter(Boolean).join(", ")}</p>}
                  </div>
                  <div className="space-y-2 mt-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Specialties</p>
                    <div className="flex flex-wrap gap-2">{contractor.specialties?.length > 0 ? contractor.specialties.map((item) => <Badge key={item} variant="secondary">{item}</Badge>) : <span className="text-sm text-muted-foreground">No specialties yet</span>}</div>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-border/50 pt-4">
                    <Badge variant="outline" className="capitalize">{contractor.status}</Badge>
                    <span className="text-xs text-muted-foreground">{crews.filter((crew) => crew.contractor_company_id === contractor.id).length} teams</span>
                  </div>
                </CardContent>
              </Card>
            )) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><Building className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No subcontractor companies found</p></CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="crews" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCrews.length > 0 ? filteredCrews.map((crew) => (
              <Card key={crew.id} className="stat-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>{crew.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Company: {companyMap[crew.contractor_company_id]?.name || "Unknown"}</p>
                    <p>Type: <span className="capitalize">{crew.crew_type}</span></p>
                    <p>Capacity: {crew.capacity || 1}</p>
                    {crew.project_id && <p>Project: {projects.find((project) => project.id === crew.project_id)?.name || "Assigned project"}</p>}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Specialties</p>
                    <div className="flex flex-wrap gap-2">{(crew.specialties || (crew.specialty ? [crew.specialty] : [])).length > 0 ? (crew.specialties || [crew.specialty]).filter(Boolean).map((item) => <Badge key={item} variant="secondary">{item}</Badge>) : <span className="text-sm text-muted-foreground">No specialties yet</span>}</div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Approved Tasks</p>
                    <div className="flex flex-wrap gap-2">{crew.approved_task_types?.length > 0 ? crew.approved_task_types.map((item) => <Badge key={item} variant="outline">{item}</Badge>) : <span className="text-sm text-muted-foreground">No task approvals yet</span>}</div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Members</p>
                    {crew.members?.length > 0 ? <div className="space-y-2">{crew.members.map((member) => <div key={member.id} className="text-sm text-muted-foreground flex items-center justify-between"><span>{member.full_name}</span><Badge variant="outline">{member.member_role}</Badge></div>)}</div> : <span className="text-sm text-muted-foreground">No members assigned yet</span>}
                  </div>
                </CardContent>
              </Card>
            )) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><Users className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No teams found</p></CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="workers" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredWorkers.length > 0 ? filteredWorkers.map((worker) => (
              <Card key={worker.id} className="stat-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-emerald-500" /></div>{worker.full_name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Company: {companyMap[worker.subcontractor_company_id]?.name || "Unknown"}</p>
                    {worker.role_title && <p>Role: {worker.role_title}</p>}
                    {worker.email && <p>{worker.email}</p>}
                    {worker.phone && <p>{worker.phone}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="capitalize">{worker.worker_type}</Badge>
                    <Badge variant={worker.is_portal_enabled ? "default" : "secondary"}>{worker.is_portal_enabled ? "Portal enabled" : "Portal disabled"}</Badge>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Specialties</p>
                    <div className="flex flex-wrap gap-2">{worker.specialties?.length > 0 ? worker.specialties.map((item) => <Badge key={item} variant="secondary">{item}</Badge>) : <span className="text-sm text-muted-foreground">No specialties yet</span>}</div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Approved Tasks</p>
                    <div className="flex flex-wrap gap-2">{worker.approved_task_types?.length > 0 ? worker.approved_task_types.map((item) => <Badge key={item} variant="outline">{item}</Badge>) : <span className="text-sm text-muted-foreground">No task approvals yet</span>}</div>
                  </div>
                  <div className="pt-2 border-t border-border/50 text-sm text-muted-foreground">
                    Teams: {worker.teams?.length > 0 ? worker.teams.join(", ") : "No teams yet"}
                  </div>
                </CardContent>
              </Card>
            )) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No individuals found</p></CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="availability" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {availabilityBlocks.length > 0 ? availabilityBlocks.map((block) => {
              const subject = block.subject_type === "team" ? crews.find((crew) => crew.id === block.subject_id)?.name : workers.find((worker) => worker.id === block.subject_id)?.full_name;
              return (
                <Card key={block.id} className="stat-card">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{subject || "Unknown subject"}</p>
                        <p className="text-sm text-muted-foreground capitalize">{block.subject_type}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{block.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>{new Date(block.start_date).toLocaleString()}</p>
                      <p>→ {new Date(block.end_date).toLocaleString()}</p>
                      {block.notes && <p>{block.notes}</p>}
                    </div>
                  </CardContent>
                </Card>
              );
            }) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No availability blocks yet</p></CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {bookingRequests.length > 0 ? bookingRequests.map((booking) => {
              const company = companyMap[booking.subcontractor_company_id];
              const target = booking.target_type === "team" ? crews.find((crew) => crew.id === booking.target_id)?.name : workers.find((worker) => worker.id === booking.target_id)?.full_name;
              return (
                <Card key={booking.id} className="stat-card">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold">{booking.title}</h3>
                        <p className="text-sm text-muted-foreground">{projectMap[booking.project_id]?.name || "Unknown project"}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{booking.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Company: {company?.name || "Unknown"}</p>
                      <p>Target: {target || "Unknown"}</p>
                      <p>{new Date(booking.request_start_date).toLocaleString()} → {new Date(booking.request_end_date).toLocaleString()}</p>
                      <p>Headcount: {booking.requested_headcount}</p>
                      {booking.description && <p>{booking.description}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-border/50">
                      <Button size="sm" variant="outline" onClick={() => handleUpdateBookingStatus(booking.id, "accepted")}>Accept</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateBookingStatus(booking.id, "declined")}>Decline</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateBookingStatus(booking.id, "countered")}>Counter</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateBookingStatus(booking.id, "cancelled")}>Cancel</Button>
                      <Button size="sm" variant="secondary" onClick={() => openBookingThread(booking.id)}>Thread</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No booking requests yet</p></CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {resourceAssignments.length > 0 ? resourceAssignments.map((assignment) => {
              const team = crews.find((crew) => crew.id === assignment.team_id)?.name;
              const worker = workers.find((person) => person.id === assignment.worker_id)?.full_name;
              const booking = bookingRequests.find((item) => item.id === assignment.booking_request_id);
              return (
                <Card key={assignment.id} className="stat-card">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{assignment.assignment_role || "Resource Assignment"}</p>
                        <p className="text-sm text-muted-foreground">{projectMap[assignment.project_id]?.name || "Unknown project"}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{assignment.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {team && <p>Team: {team}</p>}
                      {worker && <p>Individual: {worker}</p>}
                      {booking && <p>Booking: {booking.title}</p>}
                      <p>Hours planned: {assignment.hours_planned}</p>
                    </div>
                    <div className="pt-2 border-t border-border/50">
                      <Button size="sm" variant="secondary" onClick={() => openAssignmentThread(assignment.id)}>Thread</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : <Card className="stat-card col-span-full"><CardContent className="text-center py-12 text-muted-foreground"><Link2 className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No resource assignments yet</p></CardContent></Card>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkforcePage;
